import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { aiApi } from "@/services/api";
import { Bot, Zap, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { formatDate, formatCurrency } from "@/lib/utils";

interface AiConfig {
  id: string;
  provider: string;
  model?: string;
  maxTokens?: number;
  monthlyBudgetCents?: number;
  currentMonthUsageCents: number;
  isActive: boolean;
  lastTestedAt?: string;
  lastTestStatus?: string;
}

interface AiUsage {
  totalTokensThisMonth: number;
  totalCostCentsThisMonth: number;
  budgetCents: number | null;
  budgetUsedPercent: number;
  requestsThisMonth: number;
}

export function AiSettingsPage() {
  const [config, setConfig] = useState<AiConfig | null>(null);
  const [usage, setUsage] = useState<AiUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
    response?: string;
  } | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    provider: "anthropic",
    apiKey: "",
    model: "claude-sonnet-4-20250514",
    maxTokens: 4096,
    monthlyBudgetCents: 10000, // R$ 100,00
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [configData, usageData] = await Promise.all([
        aiApi.getConfig(),
        aiApi.getUsage(),
      ]);

      if (configData) {
        setConfig(configData);
        setFormData({
          provider: configData.provider || "anthropic",
          apiKey: "",
          model: configData.model || "claude-sonnet-4-20250514",
          maxTokens: configData.maxTokens || 4096,
          monthlyBudgetCents: configData.monthlyBudgetCents || 10000,
        });
      }

      setUsage(usageData);
    } catch (error) {
      console.error("Erro ao carregar config:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      const dataToSave = { ...formData };
      if (!dataToSave.apiKey) {
        delete (dataToSave as Record<string, unknown>).apiKey;
      }

      const result = await aiApi.updateConfig(dataToSave);
      setConfig(result);
      setTestResult({ success: true, message: "Configuracao salva com sucesso!" });
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro ao salvar",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await aiApi.test();
      setTestResult(result);
      loadData(); // Reload to get updated test status
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro no teste",
      });
    } finally {
      setTesting(false);
    }
  }

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          icon={<Bot className="w-5 h-5 sm:w-6 sm:h-6" />}
          title="Inteligência Artificial"
          description="Configuração da API Anthropic Claude"
        />
        <div className="flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Bot className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Inteligência Artificial"
        description="Configuração da API Anthropic Claude"
        stats={
          usage
            ? [
                { value: usage.requestsThisMonth, label: "Requisições" },
                { value: formatCurrency(usage.totalCostCentsThisMonth / 100), label: "Custo" },
              ]
            : undefined
        }
      />

      {/* Status Card */}
      {config && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Bot className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium">Anthropic Claude</p>
                  <p className="text-sm text-muted-foreground">
                    {config.model || "claude-sonnet-4-20250514"}
                  </p>
                </div>
              </div>
              <Badge
                variant={
                  config.lastTestStatus === "success" ? "success" : "secondary"
                }
              >
                {config.lastTestStatus === "success"
                  ? "Conectado"
                  : "Não testado"}
              </Badge>
            </div>
            {config.lastTestedAt && (
              <p className="mt-3 text-sm text-muted-foreground">
                Último teste: {formatDate(config.lastTestedAt)}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Usage Stats */}
      {usage && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Uso Este Mês</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold">
                  {usage.requestsThisMonth}
                </p>
                <p className="text-sm text-muted-foreground">Requisições</p>
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {usage.totalTokensThisMonth.toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">Tokens</p>
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {formatCurrency(usage.totalCostCentsThisMonth / 100)}
                </p>
                <p className="text-sm text-muted-foreground">Custo</p>
              </div>
            </div>

            {usage.budgetCents && (
              <div className="mt-4">
                <div className="flex justify-between text-sm mb-1">
                  <span>Orçamento</span>
                  <span>
                    {formatCurrency(usage.totalCostCentsThisMonth / 100)} /{" "}
                    {formatCurrency(usage.budgetCents / 100)}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full ${
                      usage.budgetUsedPercent > 80
                        ? "bg-red-500"
                        : usage.budgetUsedPercent > 50
                        ? "bg-yellow-500"
                        : "bg-green-500"
                    }`}
                    style={{ width: `${Math.min(usage.budgetUsedPercent, 100)}%` }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* API Config */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">API Anthropic</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium">API Key</label>
            <Input
              type="password"
              placeholder={config ? "Deixe vazio para manter atual" : "sk-ant-api..."}
              value={formData.apiKey}
              onChange={(e) =>
                setFormData({ ...formData, apiKey: e.target.value })
              }
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Modelo</label>
              <select
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={formData.model}
                onChange={(e) =>
                  setFormData({ ...formData, model: e.target.value })
                }
              >
                <option value="claude-sonnet-4-20250514">Claude Sonnet 4</option>
                <option value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet</option>
                <option value="claude-3-haiku-20240307">Claude 3 Haiku</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Max Tokens</label>
              <Input
                type="number"
                value={formData.maxTokens}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    maxTokens: parseInt(e.target.value) || 4096,
                  })
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Budget Config */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Limite de Uso</CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <label className="text-sm font-medium">Orçamento Mensal (R$)</label>
            <Input
              type="number"
              step="0.01"
              value={(formData.monthlyBudgetCents / 100).toFixed(2)}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  monthlyBudgetCents: Math.round(parseFloat(e.target.value) * 100) || 0,
                })
              }
            />
            <p className="text-xs text-muted-foreground mt-1">
              Defina 0 para desabilitar o limite
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Test Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Testar Conexão</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button onClick={handleTest} disabled={testing}>
            {testing ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            Testar Conexão
          </Button>

          {testResult && (
            <div
              className={`p-4 rounded-lg ${
                testResult.success
                  ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                  : "bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400"
              }`}
            >
              <div className="flex items-center gap-3">
                {testResult.success ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <XCircle className="w-5 h-5" />
                )}
                <span>{testResult.message}</span>
              </div>
              {testResult.response && (
                <p className="mt-2 text-sm opacity-80">
                  Resposta: {testResult.response}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} size="lg">
          {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          Salvar Configuração
        </Button>
      </div>
    </div>
  );
}
